import { onLCP, onINP, onCLS, onFCP } from 'web-vitals/attribution';
import enviro from 'enviro';
import Raven from 'raven-js';
import CartographerReporter, { THIRTY_SECONDS } from './CartographerReporter';
import { onVisibilityHidden } from '../visibility';
import { getElementSelector } from '../elementSelector';
import performanceNow from '../../vendor/performanceNow';
import PageStateTracker from '../PageStateTracker';
const WEB_VITALS_ADAPTER_ENABLED = false;
export function isWebVitalsAdapterEnabled() {
  return WEB_VITALS_ADAPTER_ENABLED || enviro.debug('react-rhumb-web-vitals-adapter') === 'true';
}
export default class WebVitalsAdapter extends CartographerReporter {
  static getInstance(options) {
    const w = window;
    if (!w[WebVitalsAdapter.WINDOW_KEY]) {
      w[WebVitalsAdapter.WINDOW_KEY] = new WebVitalsAdapter(options);
    }
    return w[WebVitalsAdapter.WINDOW_KEY];
  }
  static resetInstance() {
    window[WebVitalsAdapter.WINDOW_KEY] = undefined;
  }
  getPageState() {
    const pageStateTracker = PageStateTracker.getInstance();
    const pageStateSummary = pageStateTracker.getStateSummary();
    const currentSnapshot = pageStateTracker.getSnapshot();
    return {
      currentOnline: currentSnapshot.online,
      currentHidden: currentSnapshot.hidden,
      wasEverHidden: pageStateSummary.wasEverHidden,
      wasEverOffline: pageStateSummary.wasEverOffline
    };
  }
  constructor(options) {
    super(options);
    this.debounceFlush = () => {
      clearTimeout(this.flushQueueTimeout);
      this.flushQueueTimeout = setTimeout(() => {
        this.flushPerformanceQueue();
      }, THIRTY_SECONDS);
    };
    this.handleLCP = metric => {
      try {
        var _attribution$target, _entryJson$entryType, _entryJson$size, _entryJson$startTime, _entryJson$renderTime, _entryJson$loadTime, _entryJson$id, _ref, _entryJson$url;
        const {
          attribution
        } = metric;
        const lcpEntry = attribution.lcpEntry;
        const entryJson = lcpEntry ? lcpEntry.toJSON() : null;
        const element = lcpEntry !== null && lcpEntry !== void 0 && lcpEntry.element ? getElementSelector(lcpEntry.element) : (_attribution$target = attribution.target) !== null && _attribution$target !== void 0 ? _attribution$target : '';
        this.pushPerformanceAction(this.lastRouteInfo, 'largest-contentful-paint', {
          entryType: (_entryJson$entryType = entryJson === null || entryJson === void 0 ? void 0 : entryJson.entryType) !== null && _entryJson$entryType !== void 0 ? _entryJson$entryType : 'largest-contentful-paint',
          size: (_entryJson$size = entryJson === null || entryJson === void 0 ? void 0 : entryJson.size) !== null && _entryJson$size !== void 0 ? _entryJson$size : 0,
          startTime: (_entryJson$startTime = entryJson === null || entryJson === void 0 ? void 0 : entryJson.startTime) !== null && _entryJson$startTime !== void 0 ? _entryJson$startTime : metric.value,
          renderTime: (_entryJson$renderTime = entryJson === null || entryJson === void 0 ? void 0 : entryJson.renderTime) !== null && _entryJson$renderTime !== void 0 ? _entryJson$renderTime : metric.value,
          loadTime: (_entryJson$loadTime = entryJson === null || entryJson === void 0 ? void 0 : entryJson.loadTime) !== null && _entryJson$loadTime !== void 0 ? _entryJson$loadTime : 0,
          id: (_entryJson$id = entryJson === null || entryJson === void 0 ? void 0 : entryJson.id) !== null && _entryJson$id !== void 0 ? _entryJson$id : '',
          url: (_ref = (_entryJson$url = entryJson === null || entryJson === void 0 ? void 0 : entryJson.url) !== null && _entryJson$url !== void 0 ? _entryJson$url : attribution.url) !== null && _ref !== void 0 ? _ref : '',
          element,
          elapsedMs: performanceNow(),
          pageState: this.getPageState(),
          webVitalsSource: true
        });
        this.debounceFlush();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    this.handleINP = metric => {
      try {
        var _metric$entries, _targetEl$hasAttribut, _targetEl$getAttribut, _attribution$interact, _interactionId, _metric$entries2, _attribution$interact2;
        const {
          attribution
        } = metric;
        const targetEl = (_metric$entries = metric.entries) === null || _metric$entries === void 0 || (_metric$entries = _metric$entries[0]) === null || _metric$entries === void 0 ? void 0 : _metric$entries.target;
        const testId = targetEl && (_targetEl$hasAttribut = targetEl.hasAttribute) !== null && _targetEl$hasAttribut !== void 0 && _targetEl$hasAttribut.call(targetEl, 'data-test-id') ? (_targetEl$getAttribut = targetEl.getAttribute('data-test-id')) !== null && _targetEl$getAttribut !== void 0 ? _targetEl$getAttribut : '' : '';
        const tagName = targetEl ? getElementSelector(targetEl) : (_attribution$interact = attribution.interactionTarget) !== null && _attribution$interact !== void 0 ? _attribution$interact : '';
        const interactionId = (_interactionId = (_metric$entries2 = metric.entries) === null || _metric$entries2 === void 0 || (_metric$entries2 = _metric$entries2[0]) === null || _metric$entries2 === void 0 ? void 0 : _metric$entries2.interactionId) !== null && _interactionId !== void 0 ? _interactionId : 0;
        this.pushPerformanceAction(this.lastRouteInfo, 'event', {
          interactionId,
          interactionNames: (_attribution$interact2 = attribution.interactionType) !== null && _attribution$interact2 !== void 0 ? _attribution$interact2 : '',
          latency: metric.value,
          elapsedMs: performanceNow(),
          testId,
          tagName,
          pageState: this.getPageState(),
          webVitalsSource: true
        });
        this.debounceFlush();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    this.handleCLS = metric => {
      try {
        var _largestShift$startTi, _largestShift$hadRece, _attribution$largestS;
        const {
          attribution
        } = metric;
        const largestShift = attribution.largestShiftEntry;
        this.pushPerformanceAction(this.lastRouteInfo, 'layout-shift', {
          entryType: 'layout-shift',
          value: metric.value,
          startTime: (_largestShift$startTi = largestShift === null || largestShift === void 0 ? void 0 : largestShift.startTime) !== null && _largestShift$startTi !== void 0 ? _largestShift$startTi : 0,
          hadRecentInput: (_largestShift$hadRece = largestShift === null || largestShift === void 0 ? void 0 : largestShift.hadRecentInput) !== null && _largestShift$hadRece !== void 0 ? _largestShift$hadRece : false,
          largestShiftTarget: (_attribution$largestS = attribution.largestShiftTarget) !== null && _attribution$largestS !== void 0 ? _attribution$largestS : '',
          elapsedMs: performanceNow(),
          pageState: this.getPageState(),
          webVitalsSource: true
        });
        this.debounceFlush();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    this.handleFCP = metric => {
      try {
        var _fcpEntry$startTime;
        const fcpEntry = metric.attribution.fcpEntry;
        this.pushPerformanceAction(this.lastRouteInfo, 'paint', {
          name: 'first-contentful-paint',
          startTime: (_fcpEntry$startTime = fcpEntry === null || fcpEntry === void 0 ? void 0 : fcpEntry.startTime) !== null && _fcpEntry$startTime !== void 0 ? _fcpEntry$startTime : metric.value,
          entryType: 'paint',
          elapsedMs: performanceNow(),
          pageState: this.getPageState(),
          webVitalsSource: true
        });
        this.debounceFlush();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    this.resolved = {};
    this.stopped = false;
    const generateTarget = node => node && 'tagName' in node ? getElementSelector(node) : undefined;
    onLCP(this.handleLCP, {
      generateTarget
    });
    onINP(this.handleINP, {
      reportAllChanges: true,
      generateTarget
    });
    onCLS(this.handleCLS, {
      reportAllChanges: false,
      generateTarget
    });
    onFCP(this.handleFCP);
    onVisibilityHidden(() => {
      try {
        this.flushPerformanceQueue();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }
  report(action) {
    if (action.type === 'COMPONENT_RENDERED' || this.resolved[action.payload.entry.id] || this.stopped) {
      return;
    }
    switch (action.type) {
      case 'ROUTE_TIMEOUT_EXPIRED':
        {
          const {
            routeSpec: {
              route
            }
          } = action.payload;
          this.lastRouteInfo = {
            route
          };
          break;
        }
      case 'GLOBAL_ERROR':
      case 'ROUTE_FAILED':
        {
          const {
            entry: {
              pathname
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route
          };
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
        {
          const {
            entry: {
              pathname
            },
            extra: {
              scenario
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route,
            scenario
          };
          break;
        }
      case 'ROUTE_SUCCEEDED':
        {
          const {
            entry: {
              pathname
            },
            extra: {
              scenario
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route,
            scenario
          };
          break;
        }
      default:
    }
    switch (action.type) {
      case 'ROUTE_UNEXPECTED':
        {
          this.stopped = true;
          break;
        }
      case 'GLOBAL_ERROR':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED':
        {
          this.resolved[action.payload.entry.id] = true;
          break;
        }
      default:
    }
  }
}
WebVitalsAdapter.WINDOW_KEY = '__rhumb_web_vitals_adapter__';